import { NavLink, useParams } from 'react-router-dom'
import { ChevronLeft, ExternalLink, Circle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button, Badge } from '@/components/ui'
import { Link } from 'react-router-dom'

const appTabs = [
  { label: 'Overview', href: '' },
  { label: 'Users', href: '/users' },
  { label: 'Roles', href: '/roles' },
  { label: 'Scopes', href: '/scopes' },
  { label: 'OAuth', href: '/oauth' },
  { label: 'Webhooks', href: '/webhooks' },
  { label: 'Meters', href: '/meters' },
  { label: 'Analytics', href: '/analytics' },
  { label: 'Settings', href: '/settings' },
]

interface AppHeaderProps {
  appName: string
  appSlug: string
  status?: 'active' | 'inactive' | 'draft'
}

export function AppHeader({ appName, appSlug, status = 'active' }: AppHeaderProps) {
  const { orgSlug, appId } = useParams()
  const baseUrl = `/orgs/${orgSlug}/apps/${appId}`

  const statusColors = {
    active: 'success',
    inactive: 'default',
    draft: 'warning',
  } as const

  return (
    <div className="mb-6">
      {/* Back link and app info */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/orgs/${orgSlug}/apps`}>
              <ChevronLeft className="mr-1 h-4 w-4" />
              Apps
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-[var(--text-primary)]">
                {appName}
              </h1>
              <Badge variant={statusColors[status]}>{status}</Badge>
            </div>
            <p className="text-sm text-[var(--text-muted)]">{appSlug}</p>
          </div>
        </div>
        <Button variant="secondary" size="sm">
          <ExternalLink className="mr-2 h-4 w-4" />
          Open App
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-[var(--border-subtle)]">
        <nav className="-mb-px flex gap-1 overflow-x-auto">
          {appTabs.map((tab) => (
            <NavLink
              key={tab.href}
              to={`${baseUrl}${tab.href}`}
              end={tab.href === ''}
              className={({ isActive }) =>
                cn(
                  'flex shrink-0 items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-[var(--accent)] text-[var(--accent)]'
                    : 'border-transparent text-[var(--text-secondary)] hover:border-[var(--border-default)] hover:text-[var(--text-primary)]'
                )
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  )
}
